import {
  useRef,
  useMemo,
  useState,
  useEffect,
  useContext,
  createContext,
} from 'react';
import { useParams } from 'react-router';
import { Janus, RecordPlayJanusPlugin } from '@techteamer/janus-api';
import { useToggle } from 'react-use';
import { EventEmitter } from 'events';
import { isEmpty } from 'lodash';

import { usePersistedStore } from 'Store/persistedStore';
import { message } from 'antd';

const JanusContext = createContext();

const emitter = new EventEmitter();

export const useJanusContext = () => {
  const context = useContext(JanusContext);

  return context;
};

//TODO: export it to .env file when DevOps can support them
const url = 'wss://video.yvi.ai';

export const PluginMessageTypes = {
  Message: 'message',
  Candidate: 'candidate',
  Stop: 'stop',
};

export const PluginEvents = {
  RecordingId: 'recordingId',
  Jsep: 'jsep',
};

// Janus Websocket connections need to receive ws messages
// regularly at given intervals for the underlying ws connection
// to be maintained. The NPM package handles this requirement
// with the keepAliveIntervalMs field. Dispatching App-side
// message would be 'keepalive' and the responding Server-side
// message would be 'ack'.
export const config = {
  url,
  keepAliveIntervalMs: 10000,
};

const waitForEvent = (eventName) => {
  return new Promise((resolve) => {
    const handler = (event) => {
      emitter.off(eventName, handler); // Clean up listener
      resolve(event);
    };

    emitter.on(eventName, handler);
  });
};

// check janus isConnected for disconnecting cases
export function JanusProvider({ children }) {
  const { token } = useParams();

  const [connection] = useToggle(true);
  const [peer, setPeer] = useState(null);
  const [janus, setJanus] = useState(null);
  const [plugin, setPlugin] = useState(null);
  const [recordingId, setRecordingId] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();
  //Used to decrease server connection creations in between renders
  const isActiveJanusConnection = useRef(false);
  const { iceServers } = usePersistedStore();
  // let recordingStartTime = null;
  const [recordingStartTime, setRecordingStartTime] = useState(null)

  //bandwidth start here
  let lastBytesSent = 0;
  let lastTimestamp = 0;
  const calculateUploadSpeed = async(report) => {
    if (
      report &&
      report?.type === 'outbound-rtp' &&
      report?.kind === 'video'
    ) {
      const currentBytesSent = report?.bytesSent;
      const currentTimestamp = report?.timestamp / 1000;
      if (lastBytesSent && lastTimestamp) {
        const bytesDifference = currentBytesSent - lastBytesSent;
        const timeDifference = currentTimestamp - lastTimestamp;
        if (timeDifference > 0) {
          const uploadSpeedBps = (bytesDifference * 8) / timeDifference;
          const uploadSpeedKbps = uploadSpeedBps / 1024;
          console.log(uploadSpeedKbps,"uploadSpeedKbps");
          if(uploadSpeedKbps < 250){
            await messageApi.open({
              type: 'warning',
              content: "Having trouble with the internet speed.",
            });
          }
        }
      }
      lastBytesSent = currentBytesSent;
      lastTimestamp = currentTimestamp;
    }
  };

  const checkNetworkSpeed = async (peerConnection) => {
    if (peerConnection) {
      const stats = await peerConnection?.getStats();
      stats?.forEach((report) => {
        calculateUploadSpeed(report);
      });
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      checkNetworkSpeed(peer);
    }, 5000);

    return () => clearInterval(interval);
  }, [peer]);

  //bandwidth end here

  const initializeJanus = async () => {
    if (Boolean(isActiveJanusConnection.current)) return;

    try {
      const newJanus = new Janus(config, console);
      const newPlugin = new RecordPlayJanusPlugin(console, false);

      setJanus(newJanus);
      setPlugin(newPlugin);
      await newJanus.connect();

      await newJanus.addPlugin(newPlugin);
      await newPlugin.configure();
      newPlugin.onmessage = (data, json) => {
        /*
          NPM side: RecordPlayJanusPlugin plugin's default behavior when 
          a recording ends is deconstructing the plugin.
          Method is re-declared here to override the plugin's default 
          behavior in an effort of reusing the plugin.
          In case if the default behavior of automatic deconstruction is 
          preferred, then remove the onmessage method. 
        */
      };

      newPlugin.on(PluginEvents.RecordingId, (recordingId) => {
        setRecordingId(recordingId);
      });

      newJanus.onerror = (error) => {
        console.log(error,"errorerrorerror?");
      };

      
      isActiveJanusConnection.current = true;
      emitter.emit('janus-init');
    } catch (error) {
      console.error('Connection Init Error:', error);
    }
  };

  useEffect(() => {
    if (connection) {
      initializeJanus();
    }
  }, [connection]);

  const startRecording = async (stream) => {
    if (!isActiveJanusConnection.current) {
      await waitForEvent('janus-init');
    }

    if (!plugin || !janus || !stream || isEmpty(iceServers)) {
      console.log(
        'Recording Prereq Error: ',
        janus,
        plugin,
        iceServers,
        stream,
      );
      await messageApi.open({
        type: 'error',
        content: "Due to some technical issue, you will not be able to attend the interview right now.",
        duration: 5,
      });
      window.location.reload()
      return false;
    }

    try {
      const peerConnectionConfig = {
        iceServers,
      };

      /* 
        Prerequisite for sending streams is processing ICE candidates.
        For the stream to be sent, two SDPs need to exist in the app. peer.

        The first SDP (offer) is generated by the app's peerConnection and
        automatically sent to the Janus Server through the WebSocket connection.
        The second SDP (answer) is received and processed through an event 
        that comes from the Janus Server Connection (via plugin). 
        Once the two SDPs are present in the peerConnection, the app. peer
        begins to transmit the camera feed to the Janus Server Connection.
        
        Note: some functions like the ice candidate processing and the second 
        SDP setting function are event-driven and async; so, they will delay
        other functions.
      */
      const peerConnection = new RTCPeerConnection(peerConnectionConfig);

      //react-webcam component's stream is turned into acceptable formats
      //for the app's peerConnection. Two tracks usually show up: audio, video
      stream?.getTracks().forEach((track) => {
        console.log('stream converted to: ', track);
        peerConnection.addTrack(track);
      });

      /*
        ICE Candidate processing function; handles two phases.
        ICE Gathering Phase: Sends ICE candidates to the Janus Server.
        ICE Complete Phase: Notifies the Janus Server that ICE candidate gathering
        is complete after finding a compatible ICE Server.
      */
      peerConnection.onicecandidate = (event) => {
        try {
          if (!event.candidate || !event.candidate.candidate) {
            //ICE Complete Phase
            plugin.consume({
              type: PluginMessageTypes.Candidate,
              message: { completed: true },
            });
          } else {
            //ICE Gathering Phase
            const data = event.candidate;
            const trickleMessage = {
              candidate: data.candidate,
              sdpMid: data.sdpMid,
              sdpMLineIndex: data.sdpMLineIndex,
            };
            plugin.consume({
              type: PluginMessageTypes.Candidate,
              message: trickleMessage,
            });
          }
        } catch (error) {
          console.error('JSEP Init Error: ', error);
        }
      };

      //Handles Second SDP: Remote
      plugin.on(PluginEvents.Jsep, (jsep) => {
        try {
          console.log('second SDP:', jsep);
          // if (
          //   !['stable', 'closed'].includes(peerConnection?.signalingState)
          // ) {
            const answer = new RTCSessionDescription(jsep);
            peerConnection.setRemoteDescription(answer);
          // }
        } catch (error) {
          console.error('JSEP Exchange Error: ', error);
        }
      });

      //Handles First SDP: Local
      const offer = await peerConnection?.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true,
      });
      console.log('first SDP:', offer);
      peerConnection.setLocalDescription(offer);
      // recordingStartTime = Date.now();
      setRecordingStartTime(Date.now())
      const jsep = { type: offer.type, sdp: offer.sdp };

      console.log('new peer: ', peerConnection);

      setPeer(peerConnection);
      //Starts recording
      await plugin.consume({
        type: PluginMessageTypes.Message,
        message: { jsep },
      });
    } catch (error) {
      console.error('Recording init Error: ', error);
    }
  };

  const stopRecording = async () => {
    if (!isEmpty(plugin)) {
      try {
        // recordingStartTime = null;
        setRecordingStartTime(null)
        await plugin.consume({ type: PluginMessageTypes.Stop });
        await peer.close();
        // stopTimer()
        setRecordingId(null);
        setPeer(null);
      } catch (error) {
        console.error('Janus Cleanup Error:', error);
      }
    }
  };

  const stopSession = async () => {
    if (janus) {
      await janus.destroy();

      isActiveJanusConnection.current = false;
    }
  };

  const getCurrentRecordingTime = () => {
    console.log(recordingStartTime,"recordingStartTime");
    return recordingStartTime ? Date.now() - recordingStartTime : 0;
  }


  const contextFunctions = {
    recordingId,
    stopSession,
    stopRecording,
    startRecording,
    getCurrentRecordingTime
  };

  return (
    <JanusContext.Provider value={contextFunctions}>
      {contextHolder}
      {children}
    </JanusContext.Provider>
  );
}
